const GEMINI_API = "https://generativelanguage.googleapis.com/v1beta/models";

let keyIndex = 0;

function getKeys(apiKey) {
  if (!apiKey) return [];
  return apiKey.split("\n").map((k) => k.trim()).filter(Boolean);
}

async function fetchWithRotation(url, body, keys) {
  let idx = keyIndex % keys.length;
  for (let attempt = 0; attempt < keys.length; attempt++) {
    const key = keys[idx];
    const endpoint = url.includes("?") ? `${url}&key=${key}` : `${url}?key=${key}`;
    const res = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    if (res.status === 429 || res.status === 503 || res.status === 403) {
      idx = (idx + 1) % keys.length;
      keyIndex = idx;
      continue;
    }

    if (!res.ok) {
      const err = await res.text();
      throw new Error(`Gemini API error: ${res.status} — ${err}`);
    }

    keyIndex = idx;
    return res.json();
  }
  throw new Error("All Gemini API keys rate-limited (429). Wait a moment or add more keys in Settings.");
}

export async function getConversationalResponse({ query, history, apiKey }) {
  const keys = getKeys(apiKey);
  if (!keys.length) throw new Error("Gemini API key not configured. Go to Settings to add it.");

  const systemPrompt = `You are Cortex, a friendly AI companion for the EnergyLens open IoT energy monitoring system.
You have access to the system's ESP32 camera (TTGO T-Camera) and can control it.
You can also teach concepts about energy, IoT, and engineering in a story-driven way.

Analyze the user's latest query and determine the intent, then generate an appropriate response.

1. **Introduction Intent**: If the user is asking you to introduce yourself. Return JSON: {"intent":"introduction","topic":null,"response":"..."}

2. **Teaching Intent**: If the user wants to learn about a topic (energy, power, IoT, engineering, etc.), explain it simply. Return JSON: {"intent":"teach","topic":"the topic","response":"..."}

3. **Camera Intent**: If the user asks to take a photo, capture, snap, or show/hide the ESP32 camera feed, include the tag [cam:snap] in your response to trigger a snapshot, [cam:show] to show the feed, or [cam:hide] to hide it. Return JSON: {"intent":"chat","topic":null,"response":"Sure, I'm taking a snapshot from the ESP32 camera now. [cam:snap]"}

4. **Chat Intent**: For casual conversation. Return JSON: {"intent":"chat","topic":null,"response":"..."}

Return ONLY the JSON object, no markdown.`;

  const contents = [];
  for (const h of history) {
    contents.push({ role: h.role === "model" ? "model" : "user", parts: [{ text: h.text }] });
  }
  contents.push({ role: "user", parts: [{ text: query }] });

  const data = await fetchWithRotation(
    `${GEMINI_API}/gemini-2.5-flash:generateContent`,
    {
      system_instruction: { parts: [{ text: systemPrompt }] },
      contents,
      generationConfig: { responseMimeType: "application/json" },
    },
    keys
  );

  const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
  if (!text) throw new Error("No response from Gemini");

  try {
    return JSON.parse(text);
  } catch {
    return { intent: "chat", topic: null, response: text };
  }
}

export async function getRobotAnimation({ photoDataUri, apiKey }) {
  const keys = getKeys(apiKey);
  if (!keys.length) return { animation: "neutral" };

  const base64 = photoDataUri.split(",")[1];
  const mimeType = photoDataUri.match(/data:(.*?);/)?.[1] || "image/jpeg";

  const prompt = `You are an expert at interpreting emotions from a still image.
Analyze the main emotion or gesture in this image and choose ONE animation state:
- "happy": clearly smiling or laughing, or waving
- "angry": looks angry, upset, or frowning
- "smile": slight, polite, or neutral smile
- "mouthOpen": mouth is open, surprised or talking
- "curious": quizzical look, raised eyebrow, tilted head
- "neutral": neutral expression, or nothing clearly fits

Return ONLY a JSON object: {"animation":"<state>"}`;

  try {
    const data = await fetchWithRotation(
      `${GEMINI_API}/gemini-2.5-flash:generateContent`,
      {
        contents: [{
          role: "user",
          parts: [
            { text: prompt },
            { inline_data: { mime_type: mimeType, data: base64 } },
          ],
        }],
        generationConfig: { responseMimeType: "application/json" },
      },
      keys
    );

    const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
    if (!text) return { animation: "neutral" };

    return JSON.parse(text);
  } catch {
    return { animation: "neutral" };
  }
}
